/**
 * CEP Utilities
 * Integration with ViaCEP API for address lookup
 */

import axios from 'axios';

export interface CEPData {
  cep: string;
  logradouro: string; // street
  complemento: string;
  bairro: string; // neighborhood
  localidade: string; // city
  uf: string; // state
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
}

export interface AddressData {
  zipCode: string;
  address: string;
  neighborhood: string;
  city: string;
  state: string;
  ibgeCode: string;
}

/**
 * Fetch address data from ViaCEP API
 */
export const fetchAddressByCEP = async (cep: string): Promise<AddressData | null> => {
  try {
    // Remove formatting
    const cleanCEP = cep.replace(/\D/g, '');
    
    if (cleanCEP.length !== 8) {
      throw new Error('CEP deve ter 8 dígitos');
    }
    
    const response = await axios.get<CEPData>(`https://viacep.com.br/ws/${cleanCEP}/json/`);
    
    // ViaCEP returns { erro: true } if CEP is not found
    if ('erro' in response.data) {
      return null;
    }
    
    return {
      zipCode: response.data.cep,
      address: response.data.logradouro,
      neighborhood: response.data.bairro,
      city: response.data.localidade,
      state: response.data.uf,
      ibgeCode: response.data.ibge,
    };
  } catch (error) {
    console.error('Error fetching CEP:', error);
    return null;
  }
};

/**
 * Validate CEP format and check if it exists
 */
export const validateAndFetchCEP = async (cep: string): Promise<{
  valid: boolean;
  data?: AddressData;
  error?: string;
}> => {
  const cleanCEP = cep.replace(/\D/g, '');
  
  if (cleanCEP.length !== 8) {
    return {
      valid: false,
      error: 'CEP deve ter 8 dígitos'
    };
  }
  
  const data = await fetchAddressByCEP(cleanCEP);
  
  if (!data) {
    return {
      valid: false,
      error: 'CEP não encontrado'
    };
  }
  
  return {
    valid: true,
    data
  };
};
